"use client"

import { useState, useEffect } from "react"
import { db } from "../../firebase"
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from "firebase/firestore"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { useNavigate } from "react-router-dom"
import { Pencil, X } from "lucide-react"
import "./AdminPage.css"

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("events")

  // Event states
  const [eventTitle, setEventTitle] = useState("")
  const [eventDate, setEventDate] = useState({ day: "", month: "" })
  const [eventTime, setEventTime] = useState("")
  const [eventLocation, setEventLocation] = useState("")
  const [eventDescription, setEventDescription] = useState("")
  const [events, setEvents] = useState([])
  const [editingEvent, setEditingEvent] = useState(null)
  const [isEditingEvent, setIsEditingEvent] = useState(false)

  // Product states
  const [productName, setProductName] = useState("")
  const [productPrice, setProductPrice] = useState("")
  const [productDescription, setProductDescription] = useState("")
  const [productImage, setProductImage] = useState("")
  const [productAvailability, setProductAvailability] = useState("")
  const [products, setProducts] = useState([])
  const [editingProduct, setEditingProduct] = useState(null)
  const [isEditingProduct, setIsEditingProduct] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        navigate("/login")
      } else {
        fetchEvents()
        fetchProducts()
      }
    })

    return () => unsubscribe()
  }, [navigate])

  const fetchEvents = async () => {
    const eventsCollectionRef = collection(db, "events")
    const eventSnapshot = await getDocs(eventsCollectionRef)
    const eventList = eventSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    setEvents(eventList)
  }

  const addEvent = async (e) => {
    e.preventDefault()
    try {
      await addDoc(collection(db, "events"), {
        title: eventTitle,
        date: { day: eventDate.day, month: eventDate.month },
        time: eventTime,
        location: eventLocation,
        description: eventDescription,
      })
      alert("Event added successfully!")
      resetEventForm()
      fetchEvents()
    } catch (error) {
      alert("Error: " + error.message)
    }
  }

  const updateEvent = async (e) => {
    e.preventDefault()
    try {
      const eventRef = doc(db, "events", editingEvent.id)
      await updateDoc(eventRef, {
        title: eventTitle,
        date: { day: eventDate.day, month: eventDate.month },
        time: eventTime,
        location: eventLocation,
        description: eventDescription,
      })
      alert("Event updated successfully!")
      resetEventForm()
      setIsEditingEvent(false)
      setEditingEvent(null)
      fetchEvents()
    } catch (error) {
      alert("Update error: " + error.message)
    }
  }

  const startEditEvent = (event) => {
    setEditingEvent(event)
    setEventTitle(event.title)
    setEventDate({ day: event.date?.day || "", month: event.date?.month || "" })
    setEventTime(event.time || "")
    setEventLocation(event.location || "")
    setEventDescription(event.description || "")
    setIsEditingEvent(true)
  }

  const cancelEditEvent = () => {
    resetEventForm()
    setIsEditingEvent(false)
    setEditingEvent(null)
  }

  const resetEventForm = () => {
    setEventTitle("")
    setEventDate({ day: "", month: "" })
    setEventTime("")
    setEventLocation("")
    setEventDescription("")
  }

  const deleteEvent = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      const eventRef = doc(db, "events", id)
      await deleteDoc(eventRef)
      fetchEvents()
    }
  }

  const fetchProducts = async () => {
    const productsCollectionRef = collection(db, "products")
    const productSnapshot = await getDocs(productsCollectionRef)
    const productList = productSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    setProducts(productList)
  }

  const addProduct = async (e) => {
    e.preventDefault()
    const newProduct = {
      name: productName,
      price: productPrice,
      description: productDescription,
      imageUrl: `/images/${productImage}`,
      availability: Number(productAvailability),
    }

    try {
      await addDoc(collection(db, "products"), newProduct)
      alert("Product added successfully!")
      resetProductForm()
      fetchProducts()
    } catch (error) {
      alert("An error occurred: " + error.message)
    }
  }

  const updateProduct = async (e) => {
    e.preventDefault()
    try {
      const productRef = doc(db, "products", editingProduct.id)
      await updateDoc(productRef, {
        name: productName,
        price: productPrice,
        description: productDescription,
        imageUrl: productImage.startsWith("/images/") ? productImage : `/images/${productImage}`,
        availability: Number(productAvailability),
      })
      alert("Product updated successfully!")
      resetProductForm()
      setIsEditingProduct(false)
      setEditingProduct(null)
      fetchProducts()
    } catch (error) {
      alert("Update error: " + error.message)
    }
  }

  const startEditProduct = (product) => {
    setEditingProduct(product)
    setProductName(product.name)
    setProductPrice(product.price)
    setProductDescription(product.description)
    // Extract just the filename from the path
    setProductImage(product.imageUrl)
    setProductAvailability(product.availability.toString())
    setIsEditingProduct(true)
  }

  const cancelEditProduct = () => {
    resetProductForm()
    setIsEditingProduct(false)
    setEditingProduct(null)
  }

  const resetProductForm = () => {
    setProductName("")
    setProductPrice("")
    setProductDescription("")
    setProductImage("")
    setProductAvailability("")
  }

  const deleteProduct = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      const productRef = doc(db, "products", id)
      await deleteDoc(productRef)
      fetchProducts()
    }
  }

  return (
    <div className="admin-container">
      <h1 className="admin-title">Admin Panel</h1>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          Events
        </button>
        <button
          className={`tab-button ${activeTab === "products" ? "active" : ""}`}
          onClick={() => setActiveTab("products")}
        >
          Products
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "events" && (
          <div className="events-tab">
            <div className="admin-card">
              <h2 className="card-title">{isEditingEvent ? "Edit Event" : "Add New Event"}</h2>
              <form onSubmit={isEditingEvent ? updateEvent : addEvent} className="admin-form">
                <div className="form-group">
                  <label htmlFor="eventTitle">Event Title</label>
                  <input
                    id="eventTitle"
                    type="text"
                    placeholder="Event Title"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="eventDay">Day</label>
                    <input
                      id="eventDay"
                      type="text"
                      placeholder="Day"
                      value={eventDate.day}
                      onChange={(e) => setEventDate({ ...eventDate, day: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="eventMonth">Month</label>
                    <input
                      id="eventMonth"
                      type="text"
                      placeholder="Month"
                      value={eventDate.month}
                      onChange={(e) => setEventDate({ ...eventDate, month: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="eventTime">Time</label>
                    <input
                      id="eventTime"
                      type="text"
                      placeholder="Time"
                      value={eventTime}
                      onChange={(e) => setEventTime(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="eventLocation">Location</label>
                    <input
                      id="eventLocation"
                      type="text"
                      placeholder="Location"
                      value={eventLocation}
                      onChange={(e) => setEventLocation(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="eventDescription">Description</label>
                  <textarea
                    id="eventDescription"
                    placeholder="Description"
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    {isEditingEvent ? "Update Event" : "Add Event"}
                  </button>
                  {isEditingEvent && (
                    <button type="button" className="cancel-btn" onClick={cancelEditEvent}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="admin-card">
              <h2 className="card-title">Existing Events</h2>
              {events.length === 0 ? (
                <p className="no-items">No events found.</p>
              ) : (
                <div className="items-grid">
                  {events.map((event) => (
                    <div key={event.id} className="item-card">
                      <div className="item-header">
                        <h3>{event.title}</h3>
                        <div className="item-actions">
                          <button onClick={() => startEditEvent(event)} className="edit-btn" aria-label="Edit event">
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => deleteEvent(event.id)}
                            className="delete-btn"
                            aria-label="Delete event"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="item-details">
                        <p className="item-date">
                          <span className="detail-label">Date:</span> {event.date?.day} {event.date?.month}
                        </p>
                        <p className="item-time">
                          <span className="detail-label">Time:</span> {event.time}
                        </p>
                        <p className="item-location">
                          <span className="detail-label">Location:</span> {event.location}
                        </p>
                        {event.description && (
                          <p className="item-description">
                            <span className="detail-label">Description:</span> {event.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "products" && (
          <div className="products-tab">
            <div className="admin-card">
              <h2 className="card-title">{isEditingProduct ? "Edit Product" : "Add New Product"}</h2>
              <form onSubmit={isEditingProduct ? updateProduct : addProduct} className="admin-form">
                <div className="form-group">
                  <label htmlFor="productName">Product Name</label>
                  <input
                    id="productName"
                    type="text"
                    placeholder="Product Name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="productPrice">Price ($)</label>
                    <input
                      id="productPrice"
                      type="number"
                      placeholder="Price ($)"
                      value={productPrice}
                      onChange={(e) => setProductPrice(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="productAvailability">Available Stock</label>
                    <input
                      id="productAvailability"
                      type="number"
                      placeholder="Available Stock"
                      value={productAvailability}
                      onChange={(e) => setProductAvailability(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="productImage">Image Name</label>
                  <input
                    id="productImage"
                    type="text"
                    placeholder="Image name (e.g. knife.jpg)"
                    value={productImage.replace("/images/", "")}
                    onChange={(e) => setProductImage(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="productDescription">Product Description</label>
                  <textarea
                    id="productDescription"
                    placeholder="Product Description"
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    {isEditingProduct ? "Update Product" : "Add Product"}
                  </button>
                  {isEditingProduct && (
                    <button type="button" className="cancel-btn" onClick={cancelEditProduct}>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="admin-card">
              <h2 className="card-title">Existing Products</h2>
              {products.length === 0 ? (
                <p className="no-items">No products found.</p>
              ) : (
                <div className="items-grid">
                  {products.map((product) => (
                    <div key={product.id} className="item-card product-item">
                      <div className="item-header">
                        <h3>{product.name}</h3>
                        <div className="item-actions">
                          <button
                            onClick={() => startEditProduct(product)}
                            className="edit-btn"
                            aria-label="Edit product"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => deleteProduct(product.id)}
                            className="delete-btn"
                            aria-label="Delete product"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </div>
                      <div className="product-image-container">
                        <img
                          src={process.env.PUBLIC_URL + product.imageUrl || "/placeholder.svg"}
                          alt={product.name}
                          className="product-image"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = "/placeholder.svg?height=200&width=200"
                          }}
                        />
                      </div>
                      <div className="item-details">
                        <p className="item-price">
                          <span className="detail-label">Price:</span> ${product.price}
                        </p>
                        <p className="item-stock">
                          <span className="detail-label">Stock:</span> {product.availability} pcs
                        </p>
                        {product.description && (
                          <p className="item-description">
                            <span className="detail-label">Description:</span> {product.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPanel
